import React, { useMemo } from "react";
import { Card, Space, Button, DatePicker, TimePicker, Segmented, Popover } from "antd";
import { RiQuestionLine } from "react-icons/ri";
import dayjs from "dayjs";

const GRANULARITY_OPTIONS = [
  { label: "細 3s", value: 3 },
  { label: "中 10s", value: 10 },
  { label: "粗 60s", value: 60 },
  { label: "極粗 300s", value: 300 },
];

const presetRange = (key) => {
  const now = dayjs();
  if (key === "hour") {
    return { startMs: now.subtract(1, "hour").valueOf(), endMs: now.valueOf() };
  }
  // 今日: local midnight through now.
  return { startMs: now.startOf("day").valueOf(), endMs: now.valueOf() };
};

const GRANULARITY_HINT = (
  <div style={{ maxWidth: 280, fontSize: 13 }}>
    <p style={{ marginBottom: 8 }}>
      精細度是回放的取樣間隔：每個間隔取該時段內最後一筆位置。 間隔越細，軌跡越精確，資料量也越大。
    </p>
    <p style={{ marginBottom: 8 }}>
      調整日期範圍時會自動套用建議值（1 小時內 10 秒、8 小時內 60 秒、更長 300 秒），之後可自行調整。
    </p>
    <p style={{ marginBottom: 0, color: "#8c8c8c" }}>
      若範圍過大導致資料量超出上限，系統會自動調整為可行的間隔並提示。
    </p>
  </div>
);

// Range + granularity picker for the workspace's shared time context. Every
// change goes through `onRangeChange` / `onIntervalChange`, which are wired
// to timeContext.setRange / setIntervalS in the core -- this component holds
// no range/interval state of its own.
//
// One DATE + a TIME range (rather than a single datetime RangePicker): the
// canvas replay window is capped at 24h anyway, so a single-day model makes
// the cap structural for picker-driven changes and keeps the picker to one
// calendar. Presets may still produce a cross-midnight range (最近一小時 just
// after 00:00); the pickers then display the start day's slice, which is why
// the time picker sets order={false}.
const RangeControls = ({ range, intervalS, onRangeChange, onIntervalChange }) => {
  // MEMOIZED ON THE MS VALUES, deliberately: rc-picker re-syncs its internal
  // (uncommitted) selection whenever the `value` prop's identity changes.
  // Fresh dayjs instances on every render meant every parent re-render --
  // including the 1Hz coarse-playhead flush during playback -- discarded
  // whatever the operator was mid-way through picking and snapped the time
  // columns back to the committed value. Stable identities make re-renders
  // invisible to the picker; only a real range change re-syncs it.
  const startMs = range?.startMs ?? null;
  const endMs = range?.endMs ?? null;
  const day = useMemo(() => dayjs(startMs ?? Date.now()).startOf("day"), [startMs]);
  const startTime = useMemo(() => dayjs(startMs ?? Date.now()), [startMs]);
  const endTime = useMemo(() => dayjs(endMs ?? Date.now()), [endMs]);
  const timeValue = useMemo(() => [startTime, endTime], [startTime, endTime]);

  const commit = (nextDay, nextStart, nextEnd) => {
    const at = (t) =>
      nextDay
        .hour(t.hour())
        .minute(t.minute())
        .second(t.second())
        .millisecond(0);
    let startMs = at(nextStart).valueOf();
    let endMs = at(nextEnd).valueOf();
    if (endMs <= startMs) endMs = nextDay.endOf("day").valueOf();
    onRangeChange({ startMs, endMs });
  };

  const handleDateChange = (nextDay) => {
    if (!nextDay) return;
    commit(nextDay.startOf("day"), startTime, endTime);
  };

  const handleTimeChange = (values) => {
    if (!values || values.length !== 2 || !values[0] || !values[1]) return;
    commit(day, values[0], values[1]);
  };

  return (
    <Card size="small" bordered>
      <Space wrap size={12}>
        <Space size={8}>
          <Button size="small" onClick={() => onRangeChange(presetRange("hour"))}>
            最近一小時
          </Button>
          <Button size="small" onClick={() => onRangeChange(presetRange("today"))}>
            今日
          </Button>
        </Space>

        <DatePicker
          size="small"
          value={day}
          onChange={handleDateChange}
          allowClear={false}
        />
        <TimePicker.RangePicker
          size="small"
          value={timeValue}
          onChange={handleTimeChange}
          allowClear={false}
          order={false}
        />

        <Space size={8} align="center">
          <Space size={4} align="center">
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>精細度</span>
            <Popover content={GRANULARITY_HINT} title="精細度說明" trigger={["hover", "click"]}>
              <RiQuestionLine
                aria-label="精細度說明"
                style={{ color: "#8c8c8c", cursor: "help", verticalAlign: "middle" }}
              />
            </Popover>
          </Space>
          <Segmented
            size="small"
            value={intervalS}
            onChange={onIntervalChange}
            options={GRANULARITY_OPTIONS}
          />
        </Space>
      </Space>
    </Card>
  );
};

export default RangeControls;
